import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, ShieldCheck } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SYMBOLS = { usd: "$", eur: "€", gbp: "£", inr: "₹", aed: "AED ", cad: "C$", aud: "A$", jpy: "¥" };

export default function PayRequest() {
  const { token } = useParams();
  const [req, setReq] = useState(null);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/payment-requests/${token}`)
      .then((r) => setReq(r.data))
      .catch(() => setError("This payment link is not valid or has been removed."));
  }, [token]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/request/checkout`, {
        token,
        origin_url: window.location.origin,
        payer_email: email || null,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start the payment");
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0F172A] px-4 py-10" data-testid="pay-request-page">
      <div className="w-full max-w-md border border-slate-700 bg-white p-6 shadow-2xl">
        <div className="font-display text-lg font-bold tracking-tight">UNITY &amp; UTILITY</div>
        <div className="overline text-[0.6rem] text-[#002FA7]">Secure payment request</div>

        {error && <p className="mt-6 text-sm text-[#FF3B30]" data-testid="pay-error">{error}</p>}

        {req && (
          <>
            <div className="mt-6 border-t border-border pt-4">
              <div className="text-sm text-slate-500">{req.reference} · requested by {req.requested_by_name}</div>
              <h1 className="mt-1 font-display text-2xl font-bold" data-testid="pay-description">{req.description}</h1>
              <div className="mt-3 font-mono text-3xl font-bold text-[#002FA7]" data-testid="pay-amount">
                {SYMBOLS[req.currency] || "$"}
                {Number(req.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              {req.due_date && <div className="mt-1 text-xs text-slate-500">Due {req.due_date}</div>}
              {req.note && <p className="mt-3 text-sm text-slate-600">{req.note}</p>}
            </div>

            {req.status === "paid" ? (
              <div className="mt-6 border border-[#A7E3CE] bg-[#E6F7F1] px-3 py-3 text-sm font-semibold text-[#0B7A5B]" data-testid="pay-already-paid">
                This request has already been paid. Thank you.
              </div>
            ) : req.status === "cancelled" ? (
              <div className="mt-6 border border-border bg-slate-50 px-3 py-3 text-sm text-slate-600" data-testid="pay-cancelled">
                This payment request was cancelled.
              </div>
            ) : (
              <div className="mt-6 space-y-3">
                <div>
                  <Label>Your email (for the receipt)</Label>
                  <Input data-testid="pay-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
                </div>
                <Button className="w-full" data-testid="pay-now-button" onClick={pay} disabled={busy}>
                  <CreditCard size={16} className="mr-1.5" /> {busy ? "Opening checkout…" : "Pay now"}
                </Button>
                <p className="flex items-center gap-1.5 text-xs text-slate-500">
                  <ShieldCheck size={14} /> Card and wallet payments processed securely by Stripe.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
